import type { IntlShape } from 'react-intl'
import { FlightViewTypes } from 'constants/flight'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'

type FlightReviewLeg = App.Checkout.FlightItem['flights'][number]

export function getFlightReviewTitle(
  intl: IntlShape,
  viewType: App.Checkout.FlightItem['viewType'] | undefined,
  hasReturnLeg = false,
): string {
  switch (viewType) {
    case FlightViewTypes.ONE_WAY:
      return intl.formatMessage({
        id: 'HicELZ331FBHEbnOm3WE',
        defaultMessage: 'One-way flight',
        description: 'Title for one-way flight item in the multi-cart review tile',
      })
    case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
    case FlightViewTypes.TWO_ONE_WAYS:
    case FlightViewTypes.RETURN:
      return intl.formatMessage({
        id: 'tRaKUBZvziM0lZjFphjB',
        defaultMessage: 'Return flights',
        description: 'Title for return flights item in the multi-cart review tile',
      })
    case FlightViewTypes.MULTI_CITY:
      return intl.formatMessage({
        id: 'f2WKL4fniyx-hStpOoj7',
        defaultMessage: 'Multi-city flights',
        description: 'Title for multi-city flights item in the multi-cart review tile',
      })
    default:
      return hasReturnLeg
        ? intl.formatMessage({
            id: 'tRaKUBZvziM0lZjFphjB',
            defaultMessage: 'Return flights',
            description: 'Title for return flights item in the multi-cart review tile',
          })
        : intl.formatMessage({
            id: 'HicELZ331FBHEbnOm3WE',
            defaultMessage: 'One-way flight',
            description: 'Title for one-way flight item in the multi-cart review tile',
          })
  }
}

function formatFlightDate(date: string | undefined, locale: string): string | undefined {
  if (!date) return undefined
  return intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, locale)
}

export function getFlightReviewDateSummary(
  intl: IntlShape,
  flights: ReadonlyArray<FlightReviewLeg>,
  viewType: App.Checkout.FlightItem['viewType'] | undefined,
): string {
  const firstFlight = flights[0]
  if (!firstFlight?.departingDate || !firstFlight.departingAirportName || !firstFlight.arrivalAirportName) {
    return ''
  }

  const startDate = formatFlightDate(firstFlight.departingDate, intl.locale)
  if (!startDate) return ''

  switch (viewType) {
    case FlightViewTypes.ONE_WAY:
      return intl.formatMessage({
        id: '6DNWWEdp--Jolu13faS5',
        defaultMessage: '{date} · {departingAirport} to {arrivalAirport}',
        description: 'One-way flight summary showing date, departure and arrival airports',
      }, {
        date: startDate,
        departingAirport: firstFlight.departingAirportName,
        arrivalAirport: firstFlight.arrivalAirportName,
      })
    case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
    case FlightViewTypes.TWO_ONE_WAYS:
    case FlightViewTypes.RETURN:
    case FlightViewTypes.MULTI_CITY: {
      const lastFlight = flights[flights.length - 1]
      if (!lastFlight?.departingDate || !lastFlight.arrivalAirportName) {
        return ''
      }

      const endDate = formatFlightDate(lastFlight.departingDate, intl.locale)
      if (!endDate) return ''

      return intl.formatMessage({
        id: 'myZf8AXBjGfIbm_BuqHR',
        defaultMessage: '{startDate} - {endDate} · {fromCity} to {toCity}',
        description: 'Multi-leg flight summary showing date range, departure and arrival cities',
      }, {
        startDate,
        endDate,
        fromCity: firstFlight.departingAirportName,
        toCity: viewType === FlightViewTypes.MULTI_CITY ? lastFlight.arrivalAirportName : firstFlight.arrivalAirportName,
      })
    }
    default:
      return ''
  }
}

export function getFlightReviewOccupancy(
  intl: IntlShape,
  occupants: App.Occupants | undefined,
  hidePassengerCount = false,
): string {
  if (hidePassengerCount || !occupants) return ''

  const hasInfants = (occupants.infants ?? 0) > 0
  const formattedOccupants = formatOccupantsShort(occupants)
  if (hasInfants) {
    return intl.formatMessage(
      {
        id: 'Fx_ROe5JMxjZplam8VXk',
        defaultMessage: '{occupants} (on lap)',
        description: 'Flight occupancy summary with infant on lap indicator in cart review tile',
      },
      { occupants: formattedOccupants },
    )
  }

  return formattedOccupants
}
